import { useCallback, useEffect, useRef, useState } from 'react';
import type { DownloadButtonState } from '../components/CameraDownloadIcon';
import {
  ERROR_FLASH_MS,
  FeedbackClip,
  FeedbackMessage,
  FlashKind,
  READY_FLASH_MS,
  clipKey,
  consumeExportRequest,
  diffStatusMaps,
  generatingMessage,
  normalizeStatus,
  resolveButtonState,
  toStatusMap,
} from './clipDownloadFeedback';

interface UseClipDownloadFeedbackOptions<T extends FeedbackClip> {
  /** Persisted clip-group id; pairs with `markExportRequested`. */
  groupId: string | number;
  clips: readonly T[];
}

export interface ClipDownloadFeedback {
  /** Icon state for a slot key (see `clipKey`). */
  getButtonState: (key: string) => DownloadButtonState;
  /** The status line under the buttons, or null when there is nothing to say. */
  message: FeedbackMessage | null;
  dismissMessage: () => void;
}

/**
 * Tracks a clip group's export statuses across refetches and turns the
 * transitions into the download-button states and status messages from the
 * design (see `clipDownloadFeedback.ts`). Owns the 5 s / 3 s timers.
 */
export function useClipDownloadFeedback<T extends FeedbackClip>({
  groupId,
  clips,
}: UseClipDownloadFeedbackOptions<T>): ClipDownloadFeedback {
  const statuses = toStatusMap(clips);
  // The clip array is rebuilt on every poll; the effect below only runs when a
  // status actually changes.
  const signature = clips
    .map((clip) => `${clipKey(clip)}=${normalizeStatus(clip.status) ?? ''}`)
    .join('|');

  const statusesRef = useRef(statuses);
  statusesRef.current = statuses;

  const prevStatusesRef = useRef(statuses);
  const [timers] = useState(() => new Map<string, ReturnType<typeof setTimeout>>());
  const [flashes, setFlashes] = useState<Record<string, FlashKind>>({});
  const [message, setMessage] = useState<FeedbackMessage | null>(() => generatingMessage(statuses));

  const clearFlash = useCallback(
    (key: string) => {
      clearTimeout(timers.get(key));
      timers.delete(key);
      setFlashes((prev) => {
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [timers],
  );

  /** Drop the status line for `key` in favour of whatever is still generating. */
  const settleMessage = useCallback((key: string) => {
    setMessage((current) =>
      current?.key === key ? generatingMessage(statusesRef.current) : current,
    );
  }, []);

  const flash = useCallback(
    (key: string, kind: FlashKind, durationMs: number) => {
      clearTimeout(timers.get(key));
      setFlashes((prev) => ({ ...prev, [key]: kind }));
      timers.set(
        key,
        setTimeout(() => {
          clearFlash(key);
          // The failure line stays until dismissed; the success line goes with its check.
          if (kind === 'ready') settleMessage(key);
        }, durationMs),
      );
    },
    [timers, clearFlash, settleMessage],
  );

  useEffect(() => {
    const transitions = diffStatusMaps(prevStatusesRef.current, statuses);
    prevStatusesRef.current = statuses;

    for (const { key, kind } of transitions) {
      switch (kind) {
        case 'generating':
          clearFlash(key);
          setMessage({ kind, key });
          break;
        case 'ready': {
          flash(key, 'ready', READY_FLASH_MS);
          if (consumeExportRequest(groupId, key)) {
            setMessage({ kind, key });
          } else {
            // Someone else's export finished: show the check, say nothing.
            settleMessage(key);
          }
          break;
        }
        case 'error':
          consumeExportRequest(groupId, key);
          flash(key, 'error', ERROR_FLASH_MS);
          setMessage({ kind, key });
          break;
        case 'cleared':
          consumeExportRequest(groupId, key);
          clearFlash(key);
          settleMessage(key);
          break;
      }
    }
    // Keyed on the status signature: `statuses`/`clips` are rebuilt every render.
  }, [signature]);

  useEffect(
    () => () => {
      timers.forEach(clearTimeout);
      timers.clear();
    },
    [timers],
  );

  return {
    getButtonState: (key) => resolveButtonState(statuses[key], flashes[key]),
    message,
    // Dismissing the failure line falls back to any export still running.
    dismissMessage: () => setMessage(generatingMessage(statusesRef.current)),
  };
}

export default useClipDownloadFeedback;
